import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@app/shadcn/components/ui/button';
import { useToast } from '@app/shadcn/components/ui/use-toast';

import { useModal } from '@app/components/modal-views/context';
import ModalCloseButton from '@app/components/modal-views/modal-close-button';
import SettingsCard from '@app/components/settings/card';
import { buttonConstant } from '@app/constants/locales/button';
import { localesCommon } from '@app/constants/locales/common';
import { inviteCollaborator } from '@app/constants/locales/invite-collaborators';
import { toastMessage } from '@app/constants/locales/toast-message';
import RoleSelect from '@app/components/member/role-select';
import { memberRoles } from '@app/constants/locales/member-roles';
import { members } from '@app/constants/locales/members';
import { useWorkspacePermissions } from '@app/lib/hooks/use-workspace-permissions';
import { WorkspacePermission } from '@app/models/enums/workspace-permission';
import { WorkspaceRole, assignableRoles, roleLocale } from '@app/models/enums/workspace-role';
import { AppInput } from '@app/shadcn/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@app/shadcn/components/ui/select';
import { useAppSelector } from '@app/store/hooks';
import { useGetWorkspaceMembersQuery, useInviteToWorkspaceMutation } from '@app/store/workspaces/members-n-invitations-api';
import { MAIL_LOCALES, MailLocale, browserMailLocale } from '@app/utils/mail-locale';

// Shared by the email input and the role select so the two line up.
export const INVITE_FIELD_CLASS = 'h-10 w-full rounded border-control px-3 text-sm';

export default function InviteMemberModal() {
    const { toast } = useToast();
    const [trigger, { data, isLoading }] = useInviteToWorkspaceMutation();
    const workspace = useAppSelector((state) => state.workspace);

    const { t } = useTranslation();
    const [invitationMail, setInvitationMail] = useState('');
    const [role, setRole] = useState<WorkspaceRole>(WorkspaceRole.EDITOR);
    // The invitation mail goes to someone who may have no account yet, so we
    // don't know their language: the inviter chooses it (English or Dutch),
    // starting from the inviter's browser language.
    const [locale, setLocale] = useState<MailLocale>(() => browserMailLocale());
    // only an owner (workspace.billing) invites an owner
    const { can } = useWorkspacePermissions();
    const canGiveOwner = can(WorkspacePermission.WORKSPACE_BILLING);
    const workspaceMember = useGetWorkspaceMembersQuery({ workspaceId: workspace.id });

    const { closeModal } = useModal();
    const isMemberExist = () => {
        if (workspaceMember.data && workspaceMember.data?.filter((member) => member.email === invitationMail).length > 0) {
            return true;
        }
        return false;
    };
    const handleSendInvitation = async (e: any) => {
        e.preventDefault();
        if (!invitationMail) {
            return;
        }

        if (isMemberExist()) {
            toast({ description: t(toastMessage.emailAlreadyExist).toString(), variant: 'destructive' });
        } else {
            const response: any = await trigger({
                workspaceId: workspace.id,
                locale,
                body: {
                    role,
                    email: invitationMail
                }
            });

            if (response.data) {
                setInvitationMail('');
                toast({ description: t(toastMessage.invitationSent).toString() });
            } else if (response.error) {
                // e.g. 409: already a member, change their role instead
                const detail = response.error?.data;
                toast({ description: typeof detail === 'string' ? detail : t(toastMessage.failedToSentEmail).toString(), variant: 'destructive' });
            }
        }

        closeModal();
    };
    return (
        <SettingsCard className="relative space-y-0! px-6 py-6 pb-8 sm:px-10 sm:pb-10">
            <ModalCloseButton onClick={closeModal} className="absolute right-2 top-2 h-8 w-8" />
            <div className="text-black-900 pr-6 text-lg font-semibold leading-snug">{t(inviteCollaborator.default)}</div>
            <div className="text-black-600 pt-3 text-sm leading-relaxed">{t(inviteCollaborator.description)}</div>
            <form onSubmit={handleSendInvitation} className="flex flex-col  justify-start pt-8">
                <label htmlFor="invite-member-email" className="text-black-700 mb-2 text-sm font-medium">
                    {t(localesCommon.enterEmail)}
                </label>
                <AppInput
                    id="invite-member-email"
                    disabled={isLoading}
                    data-testid="otp-input"
                    spellCheck={false}
                    value={invitationMail}
                    type="email"
                    autoComplete="email"
                    required
                    placeholder={t(localesCommon.enterEmail)}
                    // Same box as the role select below: full width, 40px, 14px text.
                    className={INVITE_FIELD_CLASS}
                    onChange={(event) => {
                        setInvitationMail(event.target.value);
                    }}
                />
                <div className="text-black-700 mb-2 mt-4 text-sm font-medium">
                    {t(members.role)}
                </div>
                <RoleSelect value={role} roles={assignableRoles(canGiveOwner)} onChange={setRole} disabled={isLoading} ariaLabel={t(members.role)} className={`${INVITE_FIELD_CLASS} bg-white`} />
                <div className="text-black-600 mt-2 text-xs leading-relaxed">{t(roleLocale(role).description)}</div>
                <div className="text-black-600 mt-1 text-xs leading-relaxed">{t(memberRoles.inviteDescription)}</div>
                <div id="invite-member-language-label" className="text-black-700 mb-2 mt-4 text-sm font-medium">
                    {t(inviteCollaborator.language)}
                </div>
                <Select value={locale} onValueChange={(value) => setLocale(value as MailLocale)} disabled={isLoading}>
                    <SelectTrigger aria-labelledby="invite-member-language-label" className={`${INVITE_FIELD_CLASS} bg-white`}>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                        {MAIL_LOCALES.map(({ code, label }) => (
                            <SelectItem key={code} value={code} lang={code}>
                                {label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <div className="text-black-600 mt-2 text-xs leading-relaxed">{t(inviteCollaborator.languageDescription)}</div>
                <div className="mt-4 flex w-full flex-col justify-end">
                    <Button size="medium" disabled={isLoading} isLoading={isLoading} type="submit">
                        {t(buttonConstant.sendInvitation)}
                    </Button>
                </div>
            </form>
        </SettingsCard>
    );
}
